import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Alert, Paper, Grid, CircularProgress } from '@mui/material';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ContentCopy, CheckCircle, Refresh } from '@mui/icons-material';
import Button from '../../components/common/Button/Button';
import { API_BASE_URL } from '../../config';
import { trackEvent, trackPageView } from '../../utils/analytics';

const PaymentContainer = styled(Paper)`
  padding: 2rem;
  margin-bottom: 2rem;
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: 12px;
`;

const AddressBox = styled(Box)`
  background-color: ${({ theme }) => theme.palette.background.default};
  padding: 1rem;
  border-radius: 8px;
  margin: 0.5rem 0;
  word-break: break-all;
  font-family: monospace;
`;

const AmountBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.5rem 0;
`;

const AmountRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CopyButton = styled(Button)`
  min-width: 130px;
  padding: 6px 16px;
`;

const TokenIcon = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  margin-right: 8px;
  cursor: pointer;
  transition: transform 0.2s ease;
  vertical-align: middle;

  &:hover {
    transform: scale(1.1);
  }
`;

const TokenLink = styled.a`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  color: inherit;
  margin-right: 4px;
`;

const AmountDisplay = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.palette.background.default};
  border-radius: 8px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const Payment: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [checking, setChecking] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  
  const { masterKey, paymentInfo, walletId } = location.state || {};

  // Track page load
  useEffect(() => {
    trackEvent('payment_page_view', {
      payment_method: paymentInfo?.paidMethod,
      amount: paymentInfo?.amount,
      usd_value: paymentInfo?.usdValue
    });
  }, [paymentInfo]);

  useEffect(() => {
    // Track payment page view in funnel
    trackPageView(location.pathname, {
      funnel_stage: 'payment_page',
      payment_method: paymentInfo?.paidMethod,
      has_wallet_id: !!walletId
    });
  }, [location.pathname, paymentInfo, walletId]);

  const checkPaymentStatus = async () => {
    if (checking || isPaid) return;
    setChecking(true);

    try {
      trackEvent('funnel_step', {
        step_name: 'payment_status_check',
        payment_method: paymentInfo?.paidMethod,
        wallet_id: walletId
      });

      const response = await fetch(`${API_BASE_URL}/payment/crypto/status?walletId=${walletId}`);
      const data = await response.json();
      
      if (data.paid) {
        setIsPaid(true);
        trackEvent('funnel_step', {
          step_name: 'payment_success',
          payment_method: paymentInfo?.paidMethod,
          wallet_id: walletId
        });
        setTimeout(() => {
          trackEvent('funnel_step', {
            step_name: 'funnel_complete',
            payment_method: paymentInfo?.paidMethod,
            wallet_id: walletId
          });
          navigate('/wallets');
        }, 3000);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check payment status';
      trackEvent('funnel_step', {
        step_name: 'payment_check_error',
        payment_method: paymentInfo?.paidMethod,
        wallet_id: walletId,
        error: errorMessage
      });
      console.error('Error checking payment status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleCopy = async (text: string, type: 'address' | 'amount') => {
    try {
      await navigator.clipboard.writeText(text);
      trackEvent('funnel_step', {
        step_name: 'payment_info_copied',
        type,
        payment_method: paymentInfo?.paidMethod
      });
      if (type === 'address') {
        setCopiedAddress(true);
        setTimeout(() => setCopiedAddress(false), 3000);
      } else {
        setCopiedAmount(true);
        setTimeout(() => setCopiedAmount(false), 3000);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to copy text';
      trackEvent('funnel_step', {
        step_name: 'payment_copy_error',
        type,
        payment_method: paymentInfo?.paidMethod,
        error: errorMessage
      });
      console.error('Failed to copy:', error);
    }
  };

  // Redirect if no payment info is provided
  if (!masterKey || !paymentInfo || !walletId) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography variant="h4" gutterBottom>
          Complete Your Payment
        </Typography>
        

        <PaymentContainer>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Payment Amount:
              </Typography>
              <AmountBox>
                <AmountRow>
                  <AmountDisplay>
                    {paymentInfo.paidMethod === 'STR' && (
                      <TokenLink 
                        href="https://www.spacescan.io/token/STR" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <TokenIcon 
                          src="/str.webp" 
                          alt="STR Token" 
                          title="View STR Token on Spacescan"
                        />
                      </TokenLink>
                    )}
                    <Typography variant="h5" fontWeight="bold" component="span">
                      {paymentInfo.amount} {paymentInfo.paidMethod}
                    </Typography>
                  </AmountDisplay>
                  <CopyButton
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={() => handleCopy(paymentInfo.amount.toString(), 'amount')}
                  >
                    {copiedAmount ? 'Copied!' : 'Copy Amount'}
                  </CopyButton>
                </AmountRow>
                <Typography variant="body2" color="textSecondary">
                  ≈ ${paymentInfo.usdValue} USD
                </Typography>
              </AmountBox>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Send to this address:
              </Typography>
              <AddressBox>
                {paymentInfo.address}
              </AddressBox>
              <CopyButton
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={() => handleCopy(paymentInfo.address, 'address')}
              >
                {copiedAddress ? 'Copied!' : 'Copy Address'}
              </CopyButton>
            </Grid>
          </Grid>
        </PaymentContainer>

        <Alert severity="info" sx={{ mb: 3 }}>
          Please send exactly {paymentInfo.amount} {paymentInfo.paidMethod}. Sending a different amount may result in a failed payment.
        </Alert>

        <StatusBox>
          {isPaid ? (
            <>
              <CheckCircle color="success" />
              <Typography>Payment confirmed! Redirecting to wallet dashboard...</Typography>
            </>
          ) : checking ? (
            <>
              <CircularProgress size={20} />
              <Typography>Checking payment status...</Typography>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Refresh />}
              onClick={checkPaymentStatus}
              disabled={checking}
              sx={{ minWidth: '200px' }}
            >
              Check Payment Status
            </Button>
          )}
        </StatusBox>

        {!isPaid && !checking && (
          <Box mt={3} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              After sending the payment, click the button above to check if it has been confirmed.
              Once confirmed, monitoring will be automatically activated for your wallet.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Payment; 